import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export type AutoExtendConfig = {
  enabled: boolean;
  triggerSeconds: number;
  extendSeconds: number;
};

export const AUTO_EXTEND_DEFAULTS: AutoExtendConfig = {
  enabled: true,
  triggerSeconds: 30,
  extendSeconds: 30,
};

export const AUTO_EXTEND_KEYS = [
  "auto_extend_enabled",
  "auto_extend_trigger_seconds",
  "auto_extend_seconds",
] as const;

function toBool(v: unknown, fallback: boolean) {
  if (typeof v === "boolean") return v;
  if (typeof v === "string") return v === "true";
  return fallback;
}
function toInt(v: unknown, fallback: number) {
  const n = typeof v === "number" ? v : Number(String(v));
  return Number.isFinite(n) && n > 0 ? Math.round(n) : fallback;
}

/** Global auto-extend defaults from platform_settings (publicly readable). */
export function useAutoExtendSettings() {
  return useQuery({
    queryKey: ["auto-extend-settings"],
    staleTime: 5 * 60_000,
    queryFn: async (): Promise<AutoExtendConfig> => {
      const { data } = await supabase
        .from("platform_settings")
        .select("key,value")
        .in("key", AUTO_EXTEND_KEYS as unknown as string[]);
      const map = new Map((data ?? []).map((r) => [r.key, r.value as unknown]));
      return {
        enabled: toBool(map.get("auto_extend_enabled"), AUTO_EXTEND_DEFAULTS.enabled),
        triggerSeconds: toInt(map.get("auto_extend_trigger_seconds"), AUTO_EXTEND_DEFAULTS.triggerSeconds),
        extendSeconds: toInt(map.get("auto_extend_seconds"), AUTO_EXTEND_DEFAULTS.extendSeconds),
      };
    },
  });
}

/** Resolve the effective config for one auction (per-auction override wins). */
export function resolveAuctionAutoExtend(
  auction: {
    auto_extend_enabled?: boolean | null;
    auto_extend_trigger_seconds?: number | null;
    auto_extend_seconds?: number | null;
  } | null | undefined,
  global: AutoExtendConfig | undefined,
): AutoExtendConfig {
  const g = global ?? AUTO_EXTEND_DEFAULTS;
  return {
    enabled: auction?.auto_extend_enabled ?? g.enabled,
    triggerSeconds: auction?.auto_extend_trigger_seconds ?? g.triggerSeconds,
    extendSeconds: auction?.auto_extend_seconds ?? g.extendSeconds,
  };
}

export function autoExtendExplainer(cfg: AutoExtendConfig) {
  return `Bids placed in the final ${cfg.triggerSeconds} seconds extend the auction by ${cfg.extendSeconds} seconds to ensure fair bidding.`;
}
